import { Placement } from '@amzn/fire-tv-campaign-automation-service';
import { KatDropdown } from '@amzn/katal-react';
import { useState, useCallback } from 'react';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { AutoDismissConfirmationDialog } from 'src/components/general/AutoDismissConfirmationDialog';
import { useSelection } from 'src/contexts/SelectionContext';
import { useEmbedded } from 'src/contexts/EmbeddedContext';
import { useAssetOperationsInProgress } from 'src/hooks/useAssetOperationsInProgress';
import { PropUtils } from 'src/utils/PropUtils';
import { getPlacementsForDeviceFamily } from 'src/config/deviceFamilyPlacementConfig';
import { ProvisionedConstants, STAGE } from 'src/constants';

const PlacementSelector = () => {
  const { placement, setPlacement, deviceFamily } = useSelection();
  const isTransforming = useAssetOperationsInProgress();
  const { isEmbedded } = useEmbedded();
  const [pendingPlacement, setPendingPlacement] = useState<Placement | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);

  const handlePlacementChange = (event: KatDropdown.ChangeEvent) => {
    const newPlacement = event.detail.value as Placement;

    if (newPlacement !== placement) {
      setPendingPlacement(newPlacement);
      setShowConfirmation(true);
    }
  };

  const handleConfirmPlacementChange = useCallback(() => {
    if (pendingPlacement) {
      setPlacement(pendingPlacement);
      window.location.reload();
    }
  }, [pendingPlacement, setPlacement]);

  const handleCancelPlacementChange = () => {
    setShowConfirmation(false);
    setPendingPlacement(null);
    setDropdownKey((prev) => prev + 1);
  };

  const allowedPlacements = getPlacementsForDeviceFamily(
    deviceFamily,
    ProvisionedConstants.STAGE as STAGE,
  );
  const placementOptions = PropUtils.getTitleCasedOptions(
    PropUtils.getOptionsWithUnderscoresRemoved(
      PropUtils.createOptionsFromEnumValues(allowedPlacements, Placement),
    ),
  );

  return (
    <>
      <StyledDropdown
        key={dropdownKey}
        onChange={handlePlacementChange}
        options={placementOptions}
        label="Select Placement"
        style={{ marginRight: '25px' }}
        value={placement || Placement.FEATURE_ROTATOR}
        disabled={isTransforming || isEmbedded}
        data-testid="placement-selector"
      />

      <AutoDismissConfirmationDialog
        isVisible={showConfirmation}
        title="Confirm Placement Change"
        message="Changing the placement will reset all form data and reload the page. Do you want to continue?"
        confirmText="Continue"
        cancelText="Cancel"
        confirmVariant="primary"
        autoConfirmTimeout={10000}
        onConfirm={handleConfirmPlacementChange}
        onCancel={handleCancelPlacementChange}
        data-testid="placement-confirmation-dialog"
      />
    </>
  );
};

export { PlacementSelector };
